"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, CircleCheck as CheckCircle2, ArrowRight, Phone, Mail, Award, ShieldCheck, Search, TrendingUp, Star } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const trustHighlights = ["Direct access to Yassine Essaadi — 100% consultant led", "Google Maps 3-Pack and local search dominance", "Geo-targeted local citations and authority building", "Transparent reporting focused on real lead growth"];
const localStats = [{
  label: "Local Search Focus",
  value: "Luton & Beds",
  icon: MapPin
}, {
  label: "Direct Expertise",
  value: "No Junior Staff",
  icon: ShieldCheck
}, {
  label: "Strategy First",
  value: "Tailored to You",
  icon: TrendingUp
}];
export default function Localseohero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseohero" data-nav="dark" className="relative bg-primary text-primary-foreground pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-accent/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/50 via-primary to-primary pointer-events-none" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center" variants={containerVariants} initial="hidden" animate="visible">
          <motion.div className="lg:col-span-7 space-y-6" variants={itemVariants}>
            <div className="inline-flex items-center gap-2">
              <Badge variant="secondary" className="bg-secondary/90 text-secondary-foreground hover:bg-secondary border-none px-3.5 py-1.5 text-xs sm:text-sm font-medium rounded-full shadow-sm">
                <MapPin className="size-3.5 text-primary" />
                <span>Luton &amp; Bedfordshire Local SEO</span>
              </Badge>
              <Badge variant="outline" className="border-primary-foreground/20 text-primary-foreground px-3 py-1 text-xs font-medium rounded-full">
                100% Freelance Consultant
              </Badge>
            </div>

            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-[1.15]">
              Local SEO Consultant in Luton
            </h1>

            <p className="font-sans text-lg sm:text-xl text-primary-foreground/90 max-w-2xl leading-relaxed">
              I help businesses in Luton and across Bedfordshire secure top Google Maps rankings and drive high-intent local customer inquiries. You work directly with me—Yassine Essaadi—with no account managers, no outsourced templates, and clear, measurable search growth.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {trustHighlights.map((point, i) => <div key={i} data-index={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base text-primary-foreground/95 font-medium">
                    {point}
                  </span>
                </div>)}
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4">
              <Link to="/ContactPage">
                <Button size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 text-base font-semibold px-8 py-6 rounded-lg shadow-lg cursor-pointer transition-transform active:scale-95">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-5 ml-1" />
                </Button>
              </Link>

              <Link to="/CaseStudiesPage">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 text-base font-medium px-6 py-6 rounded-lg cursor-pointer">
                  Explore My Case Studies
                </Button>
              </Link>
            </div>

            <div className="pt-4 border-t border-primary-foreground/15 flex flex-wrap items-center gap-y-2 gap-x-6 text-xs sm:text-sm text-primary-foreground/80">
              <span className="font-semibold text-primary-foreground">Direct Access:</span>
              <a href="mailto:contact@ye-consultancy.com" className="inline-flex items-center gap-1.5 hover:text-accent transition-colors">
                <Mail className="size-4 text-accent" />
                <span>contact@ye-consultancy.com</span>
              </a>
              <a href="tel:+447490984430" className="inline-flex items-center gap-1.5 hover:text-accent transition-colors">
                <Phone className="size-4 text-accent" />
                <span>+44 7490984430</span>
              </a>
            </div>
          </motion.div>

          <motion.div className="lg:col-span-5" variants={itemVariants}>
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="bg-card text-card-foreground rounded-2xl p-6 sm:p-8 shadow-2xl border border-border relative z-10">
                <div className="flex items-center justify-between pb-6 border-b border-border">
                  <div className="flex items-center gap-3">
                    <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0 border border-primary/20">
                      <Search className="size-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-serif font-bold text-lg text-card-foreground">
                        Luton Local Search Pack
                      </h2>
                      <p className="text-xs text-muted-foreground">
                        Targeted Local Visibility &amp; Map Rankings
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="bg-muted text-muted-foreground font-mono text-xs">
                    Live Audit
                  </Badge>
                </div>

                <div className="my-6 p-4 rounded-xl bg-muted/60 border border-border flex items-center gap-4">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_portrait.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_portrait.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_portrait.png-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant in Luton" className="size-16 rounded-full object-cover border-2 border-primary shrink-0 shadow-sm" loading="lazy" />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                      Your Dedicated SEO Consultant
                    </p>
                    <p className="font-serif text-base font-bold text-card-foreground">
                      Yassine Essaadi
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Senior SEO Strategist • YE Consultancy
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="p-3.5 rounded-lg bg-background border border-border flex items-center justify-between shadow-xs">
                    <div className="flex items-center gap-3">
                      <div className="size-7 rounded-full bg-accent/20 flex items-center justify-center text-accent-foreground font-bold text-xs shrink-0">
                        #1
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground">Google Map Pack Ranking</p>
                        <p className="text-[13px] text-muted-foreground">High-intent Luton commercial queries</p>
                      </div>
                    </div>
                    <Star className="size-4 text-chart-5 fill-chart-5" />
                  </div>

                  <div className="p-3.5 rounded-lg bg-background border border-border flex items-center justify-between shadow-xs">
                    <div className="flex items-center gap-3">
                      <div className="size-7 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs shrink-0">
                        100%
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground">Direct Consultant Access</p>
                        <p className="text-[13px] text-muted-foreground">No agency gatekeepers or juniors</p>
                      </div>
                    </div>
                    <Award className="size-4 text-primary" />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-6 pt-6 border-t border-border text-center">
                  {localStats.map((stat, i) => {
                  const IconComponent = stat.icon;
                  return <div key={i} data-index={i} className="p-2 rounded-lg bg-muted/40">
                        <IconComponent className="size-4 mx-auto mb-1 text-primary" />
                        <p className="font-bold text-xs text-foreground">{stat.value}</p>
                        <p className="text-[12px] text-muted-foreground truncate">{stat.label}</p>
                      </div>;
                })}
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-accent text-accent-foreground rounded-xl p-4 shadow-xl border border-accent/30 hidden sm:flex items-center gap-3 z-20 max-w-xs">
                <ShieldCheck className="size-8 shrink-0 text-accent-foreground" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide">Verified Expertise</p>
                  <p className="text-xs opacity-90">Personalized SEO strategies with direct accountability.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}